import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ControlledCountryField } from '@/common/components/Fields/ControlledCountryField/ControlledCountryField';
import { ControlledTextField } from '@/common/components/Fields/ControlledTextField/ControlledTextField';
import { ControllerContext } from '@/common/components/Fields/ControllerContext/ControllerContext';
import { CreditCardFields } from '@/common/components/Fields/CreditCardFields/CreditCardFields';
import {
  TCardFieldInfo,
  TCardFieldsInfo,
} from '@/common/components/Fields/CreditCardFields/CreditCardFields.types';
import { Stack } from '@/common/components/Stack/Stack';
import { TCreditCardFormSchema } from './CreditCardForm.schema';

export function CreditCardForm() {
  const { t } = useTranslation();
  return (
    <Stack gap="md">
      <ControlledTextField
        name="cardHolderName"
        label={t('credit_card_form.fields.card_holder_name')}
      />
      <ControllerContext<TCreditCardFormSchema>
        render={(form) => {
          const onChange = (info: TCardFieldsInfo) => {
            const number = form.getFieldState('number');
            form.setValue('number', info.number, {
              shouldValidate: number.isTouched,
              shouldTouch: !info.number.empty,
            });
            const expiry = form.getFieldState('expiry');
            form.setValue('expiry', info.expiry, {
              shouldValidate: expiry.isTouched,
              shouldTouch: !info.expiry.empty,
            });
            const cvc = form.getFieldState('cvc');
            form.setValue('cvc', info.cvc, {
              shouldValidate: cvc.isTouched,
              shouldTouch: !info.cvc.empty,
            });
          };
          const values = form.watch(['number', 'expiry', 'cvc']);
          const info = fillCardFieldInfo(values, form.formState.errors);
          return <CreditCardFields info={info} onChange={onChange} />;
        }}
      />
      <ControlledTextField
        name="zipCode"
        label={t('credit_card_form.fields.zip_code')}
      />
      <ControlledCountryField
        name="country"
        label={t('credit_card_form.fields.country')}
      />
    </Stack>
  );
}

function fillCardFieldInfo(
  values: [TCardFieldInfo, TCardFieldInfo, TCardFieldInfo],
  errors?: FieldErrors<TCreditCardFormSchema>
) {
  return {
    number: {
      ...values[0],
      error: (values[0].error || errors?.number?.message) ?? '',
    },
    expiry: {
      ...values[1],
      error: (values[1].error || errors?.expiry?.message) ?? '',
    },
    cvc: {
      ...values[2],
      error: (values[2].error || errors?.cvc?.message) ?? '',
    },
  };
}
